// Component for editing campaign details
import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {  Save } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { PageHeader } from '@/components/ui/page-header'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { useToast } from "@/components/ui/use-toast"

type Campaign = {
    id: string
    name: string
    status: 'Active' | 'Inactive'
    custom_idea: string | null
    campaign_type: "CONVERGENCE_BASED" | "BASE" | "CUSTOM"
}

export function EditCampaign() {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const { toast } = useToast()
    const [campaign, setCampaign] = useState<Campaign | null>(null)
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [formData, setFormData] = useState<Partial<Campaign>>({})

    useEffect(() => {
        async function fetchCampaign() {
            try {
                if (!id) {
                    toast({
                        title: "Error",
                        description: 'Campaign ID is required',
                        variant: "destructive"
                    })
                    navigate('/campaigns')
                    return
                }

                const { data, error } = await supabase
                    .from('campaigns')
                    .select('id, name, status, custom_idea, campaign_type')
                    .eq('id', id)
                    .single()

                if (error) throw error
                setCampaign(data)
                setFormData(data)
            } catch (err) {
                toast({
                    title: "Error",
                    description: err instanceof Error ? err.message : 'Failed to fetch campaign',
                    variant: "destructive"
                })
                navigate('/campaigns')
            } finally {
                setLoading(false)
            }
        }

        fetchCampaign()
    }, [id, navigate, toast])

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setSaving(true)

        try {
            if (!id) {
                throw new Error('Campaign ID is required')
            }

            const { error } = await supabase
                .from('campaigns')
                .update({
                    name: formData.name,
                    status: formData.status,
                    custom_idea: formData.custom_idea
                })
                .eq('id', id)

            if (error) throw error

            toast({
                title: "Success",
                description: "Campaign updated successfully"
            })

            navigate(`/campaigns/${id}`)
        } catch (err) {
            toast({
                title: "Error",
                description: err instanceof Error ? err.message : 'Failed to update campaign',
                variant: "destructive"
            })
        } finally {
            setSaving(false)
        }
    }

    if (loading) {
        return <div>Loading...</div>
    }

    if (!campaign) {
        return <div>Campaign not found</div>
    }

    return (
        <div className="container mx-auto p-6 space-y-6">

            <PageHeader
                title="Edit Campaign"
                description="Update campaign details and settings"
            />

            <form onSubmit={handleSubmit} className="space-y-8 max-w-2xl">
                <div className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="name">Campaign Name</Label>
                        <Input
                            id="name"
                            value={formData.name || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                            required
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="status">Status</Label>
                        <Select
                            value={formData.status}
                            onValueChange={(value: 'Active' | 'Inactive') =>
                                setFormData(prev => ({ ...prev, status: value }))
                            }
                        >
                            <SelectTrigger>
                                <SelectValue placeholder="Select status" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="Active">Active</SelectItem>
                                <SelectItem value="Inactive">Inactive</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    {campaign.campaign_type === 'CUSTOM' && (
                        <div className="space-y-2">
                            <Label htmlFor="custom_idea">Custom Campaign Idea</Label>
                            <Input
                                id="custom_idea"
                                value={formData.custom_idea || ''}
                                onChange={(e) => setFormData(prev => ({ ...prev, custom_idea: e.target.value }))}
                            />
                        </div>
                    )}
                </div>

                <div className="flex gap-4">
                    <Button type="submit" disabled={saving}>
                        <Save className="h-4 w-4 mr-2" />
                        {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => navigate(`/campaigns/${id}`)}
                    >
                        Cancel
                    </Button>
                </div>
            </form>
        </div>
    )
} 